import styled, { css } from "styled-components";
import { Link } from "react-router-dom";

import { Input as SharedInput } from "../../components/IOSCompatibleInput/IOSCompatibleInput.style";
import CreateModal from "../../components/Modals/CreateModal/CreateModal";
import { pxToRem, withAlpha } from "../../styles/utils";
import { ButtonWrapper } from "../CoursesPage/CoursesPage.styles";
import { StudentMain } from "../StudentPortal/studentPortal.styles";

const standardNewsFieldFocus = css`
  border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.26)};
  background: ${({ theme }) => theme.colors.white};
  --autofill-background: ${({ theme }) => theme.colors.white};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.38)};
    background: ${({ theme }) => theme.colors.white};
  }

  &:focus,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.white};
    --autofill-background: ${({ theme }) => theme.colors.white};
    box-shadow: none;
  }
`;
export const NewsMain = styled(StudentMain)({
  position: "relative",
  flex: "1 1 auto",
  minHeight: 0,
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  fontSize: "16px",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

export const NewsShell = styled.div({
  width: "100%",
  maxWidth: "none",
  margin: 0,
});

export const NewsManageToolbar = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: pxToRem(theme.spacing.md),
  width: "100%",
  marginBottom: pxToRem(theme.spacing.md),
}));

export const NewsManageToolbarFilters = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: "1 1 auto",
  flexWrap: "wrap",
  gap: pxToRem(theme.spacing.sm),
  minWidth: 0,
}));

export const NewsManageSearchInput = styled(SharedInput)`
  flex: 1 1 320px;
  width: 100%;
  min-width: 180px;
  max-width: 420px;
  ${standardNewsFieldFocus}
`;

export const HeaderActions = styled.div(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  gap: pxToRem(theme.spacing.sm),
  flex: "0 0 auto",
  "@media (max-width: 760px)": {
    justifyContent: "flex-start",
  },
}));

export const SearchRow = styled.div(({ theme }) => ({
  marginBottom: pxToRem(theme.spacing.lg),
}));

export const SearchInput = styled(SharedInput)`
  max-width: 720px;
  ${standardNewsFieldFocus}
`;

export const LoadingSlot = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: pxToRem(120),
  padding: pxToRem(theme.spacing.lg) + " 0",
}));

export const NewsList = styled.div(({ theme }) => ({
  borderTop: pxToRem(1) + " solid " + theme.borders.divider,
}));

export const NewsRow = styled(Link)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "180px minmax(0, 1fr) auto",
  gap: "24px",
  alignItems: "center",
  minHeight: "140px",
  padding: "16px 20px",
  borderBottom: pxToRem(1) + " solid " + theme.borders.divider,
  color: theme.colors.ink,
  textDecoration: "none",
  transition: "background-color 0.15s ease",
  "&:hover": {
    background: theme.colors.brandSubtle,
  },
  "&:focus-visible": {
    outline: pxToRem(2) + " solid " + theme.colors.brand,
    outlineOffset: pxToRem(2),
  },
  "@media (max-width: 760px)": {
    gridTemplateColumns: "120px minmax(0, 1fr)",
    alignItems: "start",
  },
  "@media (max-width: 520px)": {
    gridTemplateColumns: "1fr",
  },
}));

export const Cover = styled.div(({ theme }) => ({
  width: "100%",
  aspectRatio: "16 / 9",
  overflow: "hidden",
  borderRadius: pxToRem(theme.radii.md),
  background: theme.colors.inkSurface,
  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
}));

export const CoverPlaceholder = styled.div(({ theme }) => ({
  display: "grid",
  placeItems: "center",
  width: "100%",
  height: "100%",
  color: theme.colors.inkMuted,
  fontSize: "14px",
}));

export const NewsRowBody = styled.div({
  minWidth: 0,
});

export const RowMeta = styled.div(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: pxToRem(theme.spacing.xs),
  marginBottom: pxToRem(theme.spacing.xs),
  color: theme.colors.inkMuted,
  fontSize: "14px",
}));

export const NewBadgeRow = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: pxToRem(theme.spacing.xs),
}));

export const RowTitle = styled.h2(({ theme }) => ({
  margin: 0,
  color: theme.colors.ink,
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: 1.3,
}));

export const RowExcerpt = styled.p(({ theme }) => ({
  display: "-webkit-box",
  overflow: "hidden",
  margin: pxToRem(theme.spacing.xs) + " 0 0",
  color: theme.colors.inkSoft,
  fontSize: "16px",
  lineHeight: 1.55,
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
}));

export const ArticleHeader = styled.header(({ theme }) => ({
  maxWidth: "900px",
  margin: "0 auto " + pxToRem(theme.spacing.xxl),
}));

export const ArticleNumber = styled.div(({ theme }) => ({
  marginBottom: pxToRem(theme.spacing.xs),
  color: theme.colors.inkMuted,
  fontSize: "14px",
}));

export const ArticleTitle = styled.h1(({ theme }) => ({
  margin: 0,
  color: theme.colors.ink,
  fontSize: "34px",
  fontWeight: 650,
  lineHeight: 1.15,
  "@media (max-width: 700px)": {
    fontSize: "28px",
  },
}));

export const ArticleExcerpt = styled.p(({ theme }) => ({
  margin: pxToRem(theme.spacing.md) + " 0 0",
  color: theme.colors.inkSoft,
  fontSize: "18px",
  lineHeight: 1.55,
}));

export const ArticleDate = styled.div(({ theme }) => ({
  marginTop: pxToRem(theme.spacing.md),
  color: theme.colors.inkMuted,
  fontSize: "14px",
}));

export const ArticleCover = styled.img(({ theme }) => ({
  display: "block",
  width: "100%",
  maxWidth: "980px",
  maxHeight: "520px",
  margin: "0 auto " + pxToRem(theme.spacing.xxl),
  borderRadius: pxToRem(theme.radii.lg),
  objectFit: "cover",
}));

export const ArticleBody = styled.article(({ theme }) => ({
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  color: theme.colors.ink,
  fontSize: "17px",
  lineHeight: 1.7,
  overflowWrap: "anywhere",
  p: { margin: "0 0 " + pxToRem(theme.spacing.lg) },
  h1: { margin: pxToRem(36) + " 0 " + pxToRem(theme.spacing.md), fontSize: "30px", lineHeight: 1.2 },
  h2: { margin: pxToRem(32) + " 0 " + pxToRem(theme.spacing.md), fontSize: "26px", lineHeight: 1.25 },
  h3: { margin: pxToRem(28) + " 0 " + pxToRem(theme.spacing.sm), fontSize: "22px", lineHeight: 1.3 },
  "ul, ol": { margin: "0 0 " + pxToRem(theme.spacing.lg), paddingLeft: pxToRem(26) },
  li: { marginBottom: pxToRem(theme.spacing.xs) },
  "a": { color: theme.colors.brand },
  "hr": { margin: pxToRem(32) + " 0", border: 0, borderTop: pxToRem(1) + " solid " + theme.borders.divider },
  ".news-checklist": { listStyle: "none", paddingLeft: 0 },
  ".news-checklist li": { listStyle: "none" },
  ".news-checklist-row": {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  ".news-checklist .news-checklist": {
    margin: "8px 0 0 28px",
  },
  ".news-checklist input": { marginTop: "6px", accentColor: theme.colors.brand },
  ".news-article-spacer": { height: "32px" },
  ".news-article-stars": {
    margin: "28px 0",
    color: theme.colors.inkMuted,
    fontSize: "18px",
    letterSpacing: "8px",
    textAlign: "center",
    lineHeight: 1,
  },
}));

export const ArticleQuote = styled.blockquote(({ theme }) => ({
  margin: pxToRem(theme.spacing.xl) + " 0",
  padding: pxToRem(theme.spacing.md) + " " + pxToRem(theme.spacing.lg),
  border: pxToRem(1) + " solid " + theme.borders.divider,
  borderRadius: pxToRem(theme.radii.lg),
  background: theme.colors.brandSubtle,
  cite: {
    display: "block",
    marginTop: pxToRem(theme.spacing.sm),
    color: theme.colors.inkMuted,
    fontSize: "14px",
  },
}));

export const ArticleCode = styled.pre(({ theme }) => ({
  overflowX: "auto",
  margin: pxToRem(theme.spacing.lg) + " 0",
  padding: pxToRem(theme.spacing.lg),
  borderRadius: pxToRem(theme.radii.md),
  background: theme.colors.inkSurface,
  fontSize: "14px",
  lineHeight: 1.55,
}));

export const ArticleTableWrap = styled.div(({ theme }) => ({
  overflowX: "auto",
  margin: pxToRem(theme.spacing.lg) + " 0",
  table: { width: "100%", borderCollapse: "collapse" },
  "td, th": {
    padding: pxToRem(theme.spacing.sm),
    border: pxToRem(1) + " solid " + theme.borders.divider,
    verticalAlign: "top",
  },
}));

export const ArticleFigure = styled.figure(({ theme }) => ({
  margin: pxToRem(theme.spacing.xl) + " 0",
  "audio, video": { width: "100%" },
  ".news-article-video": {
    minHeight: 0,
    height: "auto",
    aspectRatio: "16 / 9",
  },
  ".news-article-video > div": {
    minHeight: 0,
    height: "100%",
  },
  ".news-article-video video": {
    minHeight: 0,
  },
}));

export const ArticleImage = styled.img(({ theme }) => ({
  display: "block",
  maxWidth: "100%",
  maxHeight: "720px",
  margin: "0 auto",
  borderRadius: pxToRem(theme.radii.md),
  objectFit: "contain",
}));

export const NewsFileAttachment = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: pxToRem(theme.spacing.md),
  width: "100%",
  maxWidth: "680px",
  minHeight: pxToRem(76),
  margin: "0 auto",
  padding: pxToRem(theme.spacing.md) + " 0",
  color: theme.colors.ink,
}));

export const NewsFileExtension = styled.span(({ theme }) => ({
  display: "grid",
  placeItems: "center",
  flex: "0 0 auto",
  width: pxToRem(52),
  height: pxToRem(58),
  padding: pxToRem(theme.spacing.xxs),
  border: pxToRem(1) + " solid " + withAlpha(theme.colors.brand, 0.28),
  borderRadius: pxToRem(theme.radii.sm),
  background: theme.colors.brandSubtle,
  color: theme.colors.brand,
  fontSize: pxToRem(11),
  fontWeight: 700,
  letterSpacing: "0.04em",
  lineHeight: 1,
  textAlign: "center",
  textTransform: "uppercase",
}));

export const NewsFileInfo = styled.span(({ theme }) => ({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: pxToRem(theme.spacing.xxs),
  minWidth: 0,
  color: theme.colors.ink,
}));

export const NewsFileDownload = styled.a(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  width: "fit-content",
  color: theme.colors.brand,
  fontSize: pxToRem(theme.fontSize.sm),
  fontWeight: 500,
  lineHeight: 1.3,
  textDecoration: "none",
  transition: "color 0.15s ease, text-decoration-color 0.15s ease",
  "&:hover": {
    color: theme.colors.brandDark,
    textDecoration: "underline",
    textUnderlineOffset: pxToRem(2),
  },
  "&:focus-visible": {
    outline: pxToRem(2) + " solid " + theme.colors.brand,
    outlineOffset: pxToRem(2),
    borderRadius: pxToRem(theme.radii.sm),
  },
}));

export const NewsFileName = styled.strong(({ theme }) => ({
  color: theme.colors.ink,
  fontSize: pxToRem(theme.fontSize.md),
  fontWeight: 500,
  lineHeight: 1.35,
  overflowWrap: "anywhere",
}));

export const MediaCaption = styled.figcaption(({ theme }) => ({
  marginTop: pxToRem(theme.spacing.xs),
  color: theme.colors.inkMuted,
  fontSize: "14px",
  textAlign: "center",
}));

export const ArticleLinkBlock = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: pxToRem(theme.spacing.xxs),
  margin: pxToRem(theme.spacing.lg) + " 0",
}));

export const ArticleLinkTitle = styled.a(({ theme }) => ({
  color: theme.colors.brand,
  fontSize: pxToRem(theme.fontSize.lg),
  fontWeight: 600,
  lineHeight: 1.35,
  textDecoration: "none",
  textUnderlineOffset: pxToRem(3),
  transition: "color 0.15s ease, text-decoration-color 0.15s ease",
  "&:hover": {
    color: theme.colors.brandDark,
    textDecoration: "underline",
  },
  "&:focus-visible": {
    outline: pxToRem(2) + " solid " + theme.colors.brand,
    outlineOffset: pxToRem(2),
    borderRadius: pxToRem(theme.radii.sm),
  },
}));

export const ArticleLinkDescription = styled.span(({ theme }) => ({
  color: theme.colors.inkSoft,
  fontSize: pxToRem(theme.fontSize.md),
  lineHeight: 1.5,
}));

export const ArticleVideo = styled.div(({ theme }) => ({
  display: "grid",
  placeItems: "center",
  minHeight: "180px",
  borderRadius: pxToRem(theme.radii.md),
  background: theme.colors.inkSurface,
  color: theme.colors.inkMuted,
}));

export const EditorMain = styled(NewsMain)({
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

export const EditorScrollArea = styled.div(({ theme }) => ({
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  paddingBottom: pxToRem(theme.spacing.lg),
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

export const EditorBreadcrumbBar = styled.div(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 6,
  background: theme.colors.white,
  paddingBottom: pxToRem(14),
  "> nav": {
    marginBottom: 0,
  },
}));

export const EditorFooter = styled.div(({ theme }) => ({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: pxToRem(theme.spacing.md),
  minWidth: 0,
  minHeight: pxToRem(56),
  paddingTop: pxToRem(theme.spacing.sm),
  borderTop: pxToRem(1) + " solid " + theme.borders.divider,
  background: theme.colors.white,
}));

export const EditorFooterMeta = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: pxToRem(theme.spacing.xs),
  flex: "0 0 auto",
  minWidth: 0,
  whiteSpace: "nowrap",
}));

export const EditorActions = styled(ButtonWrapper)(({ theme }) => ({
  flex: "1 1 auto",
  flexWrap: "nowrap",
  gap: pxToRem(theme.spacing.xs),
  minWidth: 0,
  marginTop: 0,
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  ".news-history-action": {
    color: theme.colors.brand,
  },
  ".news-history-action:hover": {
    color: theme.colors.brand,
  },
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

export const EditorForm = styled.div(({ theme }) => ({
  display: "grid",
  gap: pxToRem(theme.spacing.md),
  marginBottom: pxToRem(theme.spacing.lg),
}));

export const FieldLabel = styled.label(({ theme }) => ({
  display: "grid",
  gap: pxToRem(theme.spacing.xs),
  color: theme.colors.ink,
  fontSize: "15px",
  fontWeight: 500,
}));

export const FieldInput = styled(SharedInput)`
  ${standardNewsFieldFocus}
`;

export const FieldTextarea = styled(SharedInput).attrs({ as: "textarea" })`
  min-height: ${pxToRem(90)};
  resize: vertical;
  line-height: 1.5;
  padding-top: ${({ theme }) => pxToRem(theme.spacing.md)};
  padding-bottom: ${({ theme }) => pxToRem(theme.spacing.md)};
  ${standardNewsFieldFocus}
`;

export const CoverControls = styled.div(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: pxToRem(theme.spacing.sm),
}));

export const CoverPreviewFrame = styled.div(({ theme }) => ({
  position: "relative",
  flex: "0 0 auto",
  width: "180px",
  aspectRatio: "16 / 9",
  overflow: "hidden",
  borderRadius: pxToRem(theme.radii.md),
  background: theme.colors.ink,
  "&:hover img, &:focus-within img": {
    filter: "brightness(0.72)",
  },
  "&:hover button, &:focus-within button": {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto",
  },
}));

export const CoverPreview = styled.img({
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "filter 0.16s ease",
  pointerEvents: "none",
});

export const CoverRemoveButton = styled.button(({ theme }) => ({
  position: "absolute",
  top: pxToRem(theme.spacing.xs),
  right: pxToRem(theme.spacing.xs),
  zIndex: 2,
  display: "grid",
  placeItems: "center",
  width: pxToRem(30),
  height: pxToRem(30),
  padding: 0,
  border: 0,
  borderRadius: "50%",
  background: withAlpha(theme.colors.inkNearBlack, 0.74),
  color: theme.colors.white,
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: pxToRem(22),
  fontWeight: 400,
  lineHeight: 1,
  opacity: 0,
  transform: "translateY(-2px)",
  transition: "opacity 0.16s ease, transform 0.16s ease, background-color 0.16s ease",
  pointerEvents: "none",
  "&:hover": {
    background: withAlpha(theme.colors.inkNearBlack, 0.88),
  },
  "&:focus-visible": {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto",
    outline: pxToRem(2) + " solid " + theme.colors.white,
    outlineOffset: pxToRem(1),
  },
}));

export const EditorFrame = styled.div(({ theme }) => ({
  minHeight: "500px",
  padding: pxToRem(theme.spacing.lg),
  border: pxToRem(1) + " solid " + theme.borders.outline,
  borderRadius: pxToRem(theme.radii.lg),
  background: theme.colors.white,
  ".ce-block__content, .ce-toolbar__content": {
    maxWidth: "920px",
  },
  ".ce-block:not(:last-child)": {
    marginBottom: "8px",
  },
  ".ce-paragraph, .cdx-list, .cdx-checklist, .ce-header, .cdx-quote, .tc-table, .ce-code__textarea": {
    fontSize: "16px",
    lineHeight: 1.55,
  },
  ".ce-popover-item, .ce-inline-tool, .news-editor-tool-title, .news-editor-video-status": {
    fontSize: "15px",
  },
  ".ce-toolbar__plus, .ce-toolbar__settings-btn, .ce-popover-item": {
    borderRadius: pxToRem(theme.radii.sm),
    color: theme.colors.ink,
  },
  ".ce-toolbar__plus:hover, .ce-toolbar__settings-btn:hover, .ce-popover-item:hover": {
    background: theme.colors.brandSubtle,
  },
  ".ce-popover": {
    "--width": "320px",
    border: pxToRem(1) + " solid " + theme.borders.outline,
    borderRadius: pxToRem(theme.radii.md),
    boxShadow: theme.shadows.sm,
    fontSize: "15px",
  },
  ".ce-popover-item": {
    minHeight: "38px",
    fontSize: "15px",
  },
  ".ce-popover-item__title": {
    minWidth: 0,
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "normal",
    lineHeight: 1.3,
  },
  ".news-editor-duplicate-icon": {
    display: "block",
    width: "18px",
    height: "18px",
  },
  ".ce-popover__search input, .ce-popover__search": {
    fontSize: "15px",
  },
  ".ce-popover__search input": {
    background: theme.colors.white,
    borderColor: withAlpha(theme.colors.ink, 0.26),
    outline: "none",
  },
  ".ce-popover__search input:focus, .ce-popover__search input:focus-visible": {
    borderColor: theme.colors.brand,
    background: theme.colors.white,
    boxShadow: "none",
  },
  ".ce-inline-toolbar": {
    border: pxToRem(1) + " solid " + theme.borders.outline,
    borderRadius: pxToRem(theme.radii.md),
    boxShadow: theme.shadows.sm,
  },
  ".ce-block--selected .ce-block__content": {
    background: theme.colors.brandSubtle,
  },
  ".cdx-list-checklist .cdx-list__checkbox-check": {
    borderColor: theme.colors.graySlate,
    background: theme.colors.white,
  },
  ".cdx-list-checklist .cdx-list__checkbox:hover .cdx-list__checkbox-check": {
    borderColor: theme.colors.brandStrong,
  },
  ".cdx-list-checklist .cdx-list__checkbox--checked .cdx-list__checkbox-check": {
    borderColor: theme.colors.brand,
    background: theme.colors.brand,
  },
  ".news-editor-delimiter": {
    display: "grid",
    placeItems: "center",
    width: "100%",
    minHeight: "40px",
    position: "relative",
  },
  ".news-editor-delimiter-line": {
    display: "block",
    width: "100%",
    height: "1px",
    background: theme.borders.divider,
  },
  ".news-editor-delimiter-stars": {
    color: theme.colors.inkMuted,
    fontSize: "18px",
    letterSpacing: "8px",
    lineHeight: 1,
  },
  ".news-editor-delimiter--space": {
    minHeight: "44px",
    border: "1px dashed " + withAlpha(theme.colors.inkMuted, 0.3),
    borderRadius: pxToRem(theme.radii.sm),
  },
  ".news-editor-delimiter-space-label": {
    color: theme.colors.inkMuted,
    fontSize: "13px",
    lineHeight: 1,
  },
  ".news-inline-tool": {
    fontWeight: 700,
  },
  ".news-editor-media-tool, .news-editor-video-tool, .news-editor-link-tool": {
    display: "grid",
    gap: pxToRem(theme.spacing.sm),
    padding: pxToRem(theme.spacing.md),
    border: pxToRem(1) + " solid " + theme.borders.outline,
    borderRadius: pxToRem(theme.radii.md),
    background: theme.colors.surfaceFaint,
  },
  ".news-editor-tool-actions": {
    display: "flex",
    flexWrap: "wrap",
    gap: pxToRem(theme.spacing.xs),
  },
  ".news-editor-tool-actions button, .news-editor-replace-button": {
    minHeight: pxToRem(theme.controlHeight.sm),
    padding: "0 " + pxToRem(theme.spacing.sm),
    border: pxToRem(1) + " solid " + theme.colors.brand,
    borderRadius: pxToRem(theme.radii.md),
    background: theme.colors.white,
    color: theme.colors.brand,
    fontFamily: "inherit",
    fontSize: pxToRem(theme.fontSize.sm),
    fontWeight: 600,
    lineHeight: 1,
    cursor: "pointer",
  },
  ".news-editor-tool-actions button:hover, .news-editor-replace-button:hover": {
    background: theme.colors.brandSubtle,
    color: theme.colors.brandDark,
  },
  ".news-editor-tool-actions button:focus-visible, .news-editor-replace-button:focus-visible": {
    outline: pxToRem(2) + " solid " + theme.colors.brand,
    outlineOffset: pxToRem(2),
  },
  ".news-editor-media-meta": {
    display: "grid",
    gap: pxToRem(theme.spacing.xs),
    minWidth: 0,
  },
  ".news-editor-media-preview-frame": {
    position: "relative",
    width: "fit-content",
    maxWidth: "100%",
    overflow: "hidden",
    borderRadius: pxToRem(theme.radii.md),
    background: theme.colors.ink,
  },
  ".news-editor-media-preview": {
    display: "block",
    maxWidth: "100%",
    maxHeight: pxToRem(360),
    borderRadius: pxToRem(theme.radii.md),
    objectFit: "contain",
    transition: "filter 0.16s ease",
    pointerEvents: "none",
  },
  ".news-editor-media-overlay-action": {
    position: "absolute",
    top: "50%",
    left: "50%",
    zIndex: 2,
    minHeight: pxToRem(theme.controlHeight.sm),
    padding: "0 " + pxToRem(theme.spacing.md),
    transform: "translate(-50%, calc(-50% + 4px))",
    border: 0,
    borderRadius: pxToRem(theme.radii.md),
    background: withAlpha(theme.colors.inkNearBlack, 0.78),
    color: theme.colors.white,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: pxToRem(theme.fontSize.sm),
    fontWeight: 600,
    lineHeight: 1,
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.16s ease, transform 0.16s ease, background-color 0.16s ease",
  },
  ".news-editor-media-preview-frame:hover .news-editor-media-preview, .news-editor-media-preview-frame:focus-within .news-editor-media-preview": {
    filter: "brightness(0.72)",
  },
  ".news-editor-media-preview-frame:hover .news-editor-media-overlay-action, .news-editor-media-preview-frame:focus-within .news-editor-media-overlay-action": {
    opacity: 1,
    transform: "translate(-50%, -50%)",
    pointerEvents: "auto",
  },
  ".news-editor-media-overlay-action:hover": {
    background: withAlpha(theme.colors.inkNearBlack, 0.9),
  },
  ".news-editor-media-overlay-action:focus-visible": {
    outline: pxToRem(2) + " solid " + theme.colors.white,
    outlineOffset: pxToRem(2),
  },
  ".news-editor-file-card": {
    display: "flex",
    alignItems: "center",
    gap: pxToRem(theme.spacing.md),
    minWidth: 0,
    padding: pxToRem(theme.spacing.md),
    border: pxToRem(1) + " solid " + theme.borders.outline,
    borderRadius: pxToRem(theme.radii.md),
    background: theme.colors.white,
  },
  ".news-editor-file-extension": {
    display: "grid",
    placeItems: "center",
    flex: "0 0 auto",
    width: pxToRem(52),
    height: pxToRem(58),
    padding: pxToRem(theme.spacing.xxs),
    border: pxToRem(1) + " solid " + withAlpha(theme.colors.brand, 0.28),
    borderRadius: pxToRem(theme.radii.sm),
    background: theme.colors.brandSubtle,
    color: theme.colors.brand,
    fontSize: pxToRem(11),
    fontWeight: 700,
    letterSpacing: "0.04em",
    lineHeight: 1,
    textAlign: "center",
  },
  ".news-editor-file-info": {
    display: "grid",
    gap: pxToRem(theme.spacing.xxs),
    minWidth: 0,
  },
  ".news-editor-file-info strong": {
    color: theme.colors.ink,
    fontSize: pxToRem(theme.fontSize.md),
    fontWeight: 500,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },
  ".news-editor-file-info span": {
    color: theme.colors.inkMuted,
    fontSize: pxToRem(theme.fontSize.sm),
  },
  ".news-editor-replace-button": {
    justifySelf: "start",
    width: "auto",
  },
  ".news-editor-caption, .news-editor-link-tool input": {
    width: "100%",
    minHeight: pxToRem(theme.controlHeight.md),
    padding: "0 " + pxToRem(theme.spacing.md),
    border: pxToRem(1) + " solid " + withAlpha(theme.colors.ink, 0.26),
    borderRadius: pxToRem(theme.radii.md),
    background: theme.colors.white,
    color: theme.colors.ink,
    fontFamily: "inherit",
    fontSize: pxToRem(theme.fontSize.md),
    lineHeight: 1.25,
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  ".news-editor-caption:hover, .news-editor-link-tool input:hover": {
    borderColor: withAlpha(theme.colors.ink, 0.38),
  },
  ".news-editor-caption:focus, .news-editor-caption:focus-visible, .news-editor-link-tool input:focus, .news-editor-link-tool input:focus-visible": {
    borderColor: theme.colors.brand,
    background: theme.colors.white,
    boxShadow: "none",
  },
  "[data-uploading=true]": {
    opacity: 0.62,
    pointerEvents: "none",
  },
  "@media (max-width: 700px)": {
    padding: pxToRem(theme.spacing.sm),
    ".ce-toolbar__content, .ce-block__content": { maxWidth: "100%" },
    ".ce-toolbar__actions": {
      right: "auto",
      left: 0,
    },
  },
}));

export const PreviewFrame = styled.div(({ theme }) => ({
  padding: pxToRem(theme.spacing.xl),
  border: pxToRem(1) + " solid " + theme.borders.outline,
  borderRadius: pxToRem(theme.radii.lg),
  background: theme.colors.white,
}));

export const SaveState = styled.span(({ theme }) => ({
  color: theme.colors.inkMuted,
  fontSize: "14px",
}));

export const NewsCreateModal = styled(CreateModal)`
  input,
  textarea {
    border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.26)};
    background: ${({ theme }) => theme.colors.white};
    --autofill-background: ${({ theme }) => theme.colors.white};
  }

  input:hover:not(:disabled),
  textarea:hover:not(:disabled) {
    border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.38)};
    background: ${({ theme }) => theme.colors.white};
  }

  input:focus,
  input:focus-visible,
  textarea:focus,
  textarea:focus-visible {
    border-color: ${({ theme }) => theme.colors.brand};
    background: ${({ theme }) => theme.colors.white};
    --autofill-background: ${({ theme }) => theme.colors.white};
    box-shadow: none;
  }
`;